import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/config";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Send, ArrowLeft, MessageSquare, Phone, Ban, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const POLL_MS = 15000;

function formatRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
}

function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AdminMessages() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPhone = searchParams.get("phone");
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activePhone, setActivePhone] = useState(initialPhone || null);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);
  const userScrolledRef = useRef(false);

  // Auth check
  useEffect(() => {
    if (!localStorage.getItem("admin_token")) nav("/admin/login");
  }, [nav]);

  const loadConvs = async () => {
    try {
      const { data } = await api.get("/admin/conversations", { params: search ? { search } : {} });
      setConversations(data.conversations);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const loadThread = async (phone, markRead = true) => {
    if (!phone) return;
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/admin/conversations/${encodeURIComponent(phone)}/messages`);
      setMessages(data.messages);
      if (markRead) {
        await api.post(`/admin/conversations/${encodeURIComponent(phone)}/mark-read`).catch(() => {});
        // Refresh convs to clear unread badge
        loadConvs();
      }
    } catch (e) {
      toast.error("Error al cargar conversación");
    } finally {
      setThreadLoading(false);
    }
  };

  useEffect(() => { loadConvs(); }, []); // eslint-disable-line
  useEffect(() => {
    const t = setInterval(() => {
      loadConvs();
      if (activePhone) loadThread(activePhone, false);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [activePhone]); // eslint-disable-line

  useEffect(() => {
    if (activePhone) loadThread(activePhone);
  }, [activePhone]); // eslint-disable-line

  useEffect(() => {
    if (!userScrolledRef.current && threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [messages]);

  const onThreadScroll = (e) => {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    userScrolledRef.current = !atBottom;
  };

  const send = async () => {
    if (!draft.trim() || !activePhone) return;
    setSending(true);
    try {
      await api.post(`/admin/conversations/${encodeURIComponent(activePhone)}/send`, { body: draft.trim() });
      setDraft("");
      userScrolledRef.current = false;
      await loadThread(activePhone, false);
      await loadConvs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar SMS");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const activeConv = conversations.find((c) => c.phone_e164 === activePhone);
  const filteredConvs = search.trim()
    ? conversations.filter((c) =>
        (c.phone_e164 || "").includes(search) ||
        ((c.registration?.full_name || "").toLowerCase().includes(search.toLowerCase()))
      )
    : conversations;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black text-white px-6 py-4 flex items-center justify-between border-b-2 border-yellow-400">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav("/admin")} data-testid="back-to-admin" className="text-white hover:text-yellow-400 hover:bg-white/10">
            <ArrowLeft className="w-4 h-4 mr-2" /> Admin
          </Button>
          <div>
            <h1 className="font-heading uppercase text-xl flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-yellow-400" /> Mensajes
            </h1>
            <div className="text-xs text-white/60">Conversaciones SMS · Twilio</div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => { loadConvs(); if (activePhone) loadThread(activePhone, false); }} className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="refresh-btn">
          <RefreshCw className="w-3 h-3 mr-1" /> Refrescar
        </Button>
      </header>

      {/* Two-pane layout */}
      <div className="flex h-[calc(100vh-73px)]">
        {/* Conversations list */}
        <aside className={`w-full md:w-96 border-r bg-white flex flex-col ${activePhone ? "hidden md:flex" : ""}`}>
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre o teléfono..."
                className="pl-9"
                data-testid="conv-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="conv-list">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
              </div>
            ) : filteredConvs.length === 0 ? (
              <div className="text-center py-12 px-6 text-gray-500">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Aún no hay conversaciones.</p>
                <p className="text-xs mt-1">Cuando un inscrito responda un SMS aparecerá aquí.</p>
              </div>
            ) : (
              filteredConvs.map((c) => {
                const name = c.registration?.full_name || "(sin nombre)";
                const initial = (name[0] || c.phone_e164?.[1] || "?").toUpperCase();
                const isActive = c.phone_e164 === activePhone;
                return (
                  <button
                    key={c.phone_e164}
                    onClick={() => setActivePhone(c.phone_e164)}
                    data-testid={`conv-${c.phone_e164}`}
                    className={`w-full flex items-start gap-3 p-3 border-b text-left transition-colors ${isActive ? "bg-orange-50 border-l-4 border-l-orange-500" : "hover:bg-gray-50"}`}
                  >
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-black font-bold flex-shrink-0 overflow-hidden">
                      {c.registration?.photo ? <img src={c.registration.photo} alt="" className="w-full h-full object-cover" /> : initial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm truncate">{name}</span>
                        <span className="text-[10px] text-gray-500 flex-shrink-0">{formatRelative(c.last_message_at)}</span>
                      </div>
                      <div className="text-xs text-gray-500 truncate">{c.phone_e164}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs truncate flex-1 ${c.unread_count > 0 ? "font-semibold text-black" : "text-gray-600"}`}>
                          {c.last_direction === "out" && <span className="text-gray-400">→ </span>}
                          {c.last_message || "(sin mensajes)"}
                        </span>
                        {c.unread_count > 0 && (
                          <span className="ml-2 bg-orange-500 text-white text-[10px] rounded-full px-2 py-0.5 font-bold flex-shrink-0">{c.unread_count}</span>
                        )}
                        {c.opted_out && (
                          <Ban className="w-3 h-3 text-red-500" title="Opt-out (STOP)" />
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Thread */}
        <main className={`flex-1 flex flex-col bg-gray-50 ${!activePhone ? "hidden md:flex" : ""}`}>
          {!activePhone ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6 text-gray-500">
              <MessageSquare className="w-16 h-16 text-gray-300 mb-3" />
              <p className="font-semibold">Selecciona una conversación</p>
              <p className="text-sm mt-1">Aquí verás los mensajes con cada inscrito.</p>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="bg-white border-b px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Button variant="ghost" size="sm" className="md:hidden -ml-2" onClick={() => setActivePhone(null)}>
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-black font-bold overflow-hidden">
                    {activeConv?.registration?.photo ? <img src={activeConv.registration.photo} alt="" className="w-full h-full object-cover" /> : (activeConv?.registration?.full_name?.[0] || "?").toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold truncate">{activeConv?.registration?.full_name || "(sin nombre)"}</div>
                    <div className="text-xs text-gray-500 truncate">{activePhone}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {activeConv?.registration && (
                    <div className="text-right text-xs text-gray-600 hidden sm:block">
                      <div>Pagado <strong className="text-green-600">${activeConv.registration.paid_amount}</strong></div>
                      <div>Resta <strong className="text-orange-600">${activeConv.registration.remaining_amount}</strong></div>
                    </div>
                  )}
                  <a href={`tel:${activePhone}`} className="w-9 h-9 rounded-lg bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center" title="Llamar">
                    <Phone className="w-4 h-4" />
                  </a>
                </div>
              </div>

              {/* Messages */}
              <div ref={threadRef} onScroll={onThreadScroll} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="thread-list">
                {threadLoading && messages.length === 0 ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-gray-400 text-sm py-8">No hay mensajes todavía. Envía el primero abajo.</div>
                ) : (
                  messages.map((m) => (
                    <motion.div
                      key={m.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}
                    >
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${
                        m.direction === "out"
                          ? "bg-gradient-to-br from-orange-500 to-yellow-500 text-white rounded-br-sm"
                          : "bg-white border rounded-bl-sm"
                      }`}>
                        <div className="whitespace-pre-wrap text-sm">{m.body}</div>
                        <div className={`text-[10px] mt-1 ${m.direction === "out" ? "text-white/70" : "text-gray-400"}`}>
                          {formatTime(m.created_at)}{m.status && m.direction === "out" ? ` · ${m.status}` : ""}
                          {m.error && <span className="text-red-200 ml-2">✗ {m.error}</span>}
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>

              {/* Composer */}
              <div className="bg-white border-t p-3">
                {activeConv?.opted_out ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-center gap-2">
                    <Ban className="w-4 h-4" /> Esta persona se dio de baja (respondió <strong>STOP</strong>). Para reactivar, dile que responda <strong>START</strong>.
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={onKeyDown}
                      placeholder="Escribe un SMS al inscrito..."
                      rows={2}
                      className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                      data-testid="composer-textarea"
                    />
                    <Button
                      onClick={send}
                      disabled={sending || !draft.trim()}
                      data-testid="composer-send"
                      className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold self-end h-[60px]"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
                <div className="text-[10px] text-gray-400 mt-1">Enter para enviar · Shift+Enter nueva línea · SMS vía Twilio</div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
